import React, {
  useState,
  useEffect,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react';
import Cobrowse from './Cobrowse';
import './Glance.css';
import { baseurl } from './Glance.constants';
import Logo from './components/Logo';
import { getSessionData, sessionInvite } from './session.service';
import { PresenceDetect } from './Presence.behavior';
import { AuthMethod, ErrorUserMessages } from './Agent.service';
import { PulseLoader } from 'react-spinners';
import CobrowseJoinButton from './components/CobrowseJoinButton';
import CobrowseCancelButton from './components/CobrowseCancelButton';
import MobileCameraShareButton from './components/MobileCameraShareButton';
import Grid from './components/Grid';
import AgentPluginLayout from './components/AgentPluginLayout';
import CobrowseInput from './components/CobrowseInput';
import MobileCameraShareInput from './components/MobileCameraShareInput';
import Loader from './components/Loader';
import ErrorMessage from './components/ErrorMessage';
import InfoMessage from './components/InfoMessage';

export interface HistoricalReportingData {
  sessionId: string;
  sessionStart: string;
  sessionEnd: string;
  duration: number;
}

interface GlanceProps {
  groupId: string;
  authUrl?: string;
  puid: string;
  openlocation?: string;
  iframeid?: string;
  gicon?: boolean;
  presence?: boolean;
  glanceBaseUrl?: string;
  visitorId?: string;
  debugMode?: boolean;
  customInvoke?: () => void;
  customOpenWindow : boolean;
  presession?: () => void;
  authHeaders?: Object;
  authBody?: URLSearchParams;
  authMethod : string;
  openWindow?: (url: string) => void;
  autoJoin?: boolean;
  errorMessage?: string;
  reportingCallback?: (sessionData: HistoricalReportingData) => void;
  reportingOrigin?: string;
  reportingExternalId?: string;
  uiVersion: number;
}

declare global {
  interface Window {
    // NOTE: we need to reference/set directly the GLANCE global var, not only GLANCE.Agent that is exposed by BA API
    GLANCE: any;
  }
}

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}
// NOTE: here Agent is the instance of AgentService or it's subclasses
let Agent: PresenceDetect;
let startSession = false;
let openWindowCallback = null;
let inSession = false;
// NOTE: we've issues with state that is not updated, likely because of not using useRef - as a workaround, using variable
let sessionStart: number;

export const GlanceV1 = (props: GlanceProps) => {
  var defaultShowError = false;
  var defaultErrorMessage = '';
  if (props.errorMessage) {
    defaultShowError = true;
    defaultErrorMessage = props.errorMessage;
  }
  const [haveKey, setHaveKey] = useState(false);
  const [haveKeyDefault, setHaveKeyDefault] = useState(false);
  const [cobrowseKey, setCobrowseKey] = useState('');
  const [visitorId, setVisitorId] = useState('');
  const [agentJoinUrl, setAgentJoinUrl] = useState(null);
  const [present, setPresent] = useState(false);
  const [presenceEnabled, setPresenceEnabled] = useState(props.presence);
  const [cobrowseEnabled, setCobrowseEnabled] = useState(false);
  const [sessionEnd, setSessionEnd] = useState(Date.now());
  const [havePhoneNumber, setHavePhoneNumber] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loadingCobrowse, setLoadingCobrowse] = useState(true);
  const [loadingMCS, setLoadingMCS] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState(defaultErrorMessage);
  const [showInfo, setShowInfo] = useState(false);
  const [showError, setShowError] = useState(defaultShowError);
  const [mobileCameraShare, setMobileCameraShare] = useState(false);
  const [autoJoin, setAutoJoin] = useState(props.autoJoin || false);

  let mobileCameraPermissionFlag = false;
  let cobrowsePermissionFlag = false;

  const handleSetPresence = (presenceFlag: boolean) => {
    const haveKeyFlag = !presenceFlag && !mobileCameraPermissionFlag;
    setPresent(presenceFlag);
    setHaveKeyDefault(haveKeyFlag);
  };

  const checkPermissions = () => {
    const mobileCameraPermission = {
      name: 'viewvideo',
      expectedValue: 'on',
      handler: (hasPermissionValue: boolean) => {
        mobileCameraPermissionFlag = hasPermissionValue;
        setMobileCameraShare(mobileCameraPermissionFlag);
        setHaveKeyDefault(!mobileCameraPermissionFlag);
        setHaveKey(!mobileCameraPermissionFlag);
      },
    };
    const cobrowsePermission = {
      name: 'cobrowse',
      expectedValue: 'on',
      handler: (hasPermissionValue: boolean) => {
        cobrowsePermissionFlag = hasPermissionValue;
        setCobrowseEnabled(cobrowsePermissionFlag);
      },
    };
    const permissions = [mobileCameraPermission, cobrowsePermission];
    Agent.checkPermissions(permissions);
    setLoadingCobrowse(false);
  };

  const errorHandlerUi = (message: string, hideError?: boolean) => {
    if (hideError) {
      setShowError(false);
      setErrorMessage('');
    } else {
      setShowError(true);
      setErrorMessage(message);
    }
  };

  const prevProps: GlanceProps = usePrevious(props);

  const createAgent = async () => {
    // NOTE: we don't have glance environment set on the BA API instance level, so we're setting relevant global var for cobrowse/presence
    window.GLANCE.Agent.webserver = `https://${baseurl[props.glanceBaseUrl]}`;

    // NOTE: intentionally doing an invalidation of the token in a specific way due to bugs / workarounds in BA API
    window.GLANCE.Authorization.setToken(true); // setting token value to true so that authorized() will return true
    await window.GLANCE.Authorization.authorize({}); // called directly to initialize _ls and skip actual token obtaining, with empty params required
    window.GLANCE.Authorization.clearToken(); // clearToken works here because _ls is initialized, otherwise - not (due to _ls is undefined until the presenceConnect is called)

    Agent = new PresenceDetect(
      {
        url: props.authUrl,
        method: props.authMethod,
        groupId: props.groupId,
        puid: props.puid,
        headers: props.authHeaders,
        body: props.authBody,
        debug: props.debugMode,
        errorHandler: errorHandlerUi,
      },
      {
        visitorId: props.visitorId,
        presenceCallback: handleSetPresence,
        permissionsCallback: checkPermissions,
        debug: props.debugMode,
        endSessionCallback: (val) => {
          return endSession(val);
        },
        startSessionCallback: (key) => startCobrowse(key),
      }
    );
    Agent.connect();
  };

  if (
    prevProps &&
    prevProps.visitorId &&
    prevProps.visitorId !== props.visitorId
  ) {
    if (props.visitorId !== visitorId) {
      setVisitorId(props.visitorId);
      setPresent(false);
    }

    Agent.updateVisitorId(props.visitorId);
  }

  if (prevProps !== props) {
    if (
      (props.authMethod === AuthMethod.SamlWithLoginkey && props.groupId) ||
      (props.authMethod === AuthMethod.Saml && props.groupId) ||
      (props.authMethod === AuthMethod.Loginkey && props.puid && props.authUrl)
    ) {
      if (props.debugMode) {
        console.log('GLANCE: Agent Plugin loading with:', {
          visitorid: props.visitorId,
          puid: props.puid,
          authurl: props.authUrl,
          openwindow: props.openWindow,
          glancebaseurl: props.glanceBaseUrl,
        });
      }
      createAgent();
    }
    openWindowCallback = props.openWindow;
  }

  useEffect(() => {
    if (present && autoJoin) {
      join();
    }
  }, [present, autoJoin]);

  // useEffect(() => {
  //   // createAgent();
  // }, []);

  // useEffect(() => {}, [
  //   present,
  //   haveKey,
  //   cobrowseKey,
  //   agentJoinUrl,
  //   loadingCobrowse,
  //   loadingMCS,
  // ]);

  // TODO: we need to refactor this - join that is responsible for everything should be reworked
  const join = async () => {
    if (inSession) {
      // end session flow
      const sessionEndReason = { reason: 'agent' };
      endSession(sessionEndReason);
    } else if (loadingCobrowse) {
      // cancel session flow
      const sessionCancelReason = { reason: 'agent' };
      cancelSession(sessionCancelReason);
      setLoadingCobrowse(false);
    } else if ((haveKeyDefault || haveKey) && cobrowseKey.length) {
      sessionStart = Date.now();
      startSession = true;
      setLoadingCobrowse(true);
      setInfoMessage('Waiting for visitor');
      setShowInfo(true);
      await startCobrowse(cobrowseKey);
    } else if (!present && !haveKey) {
      setHavePhoneNumber(false);
      setHaveKey(true);
    } else if (props.visitorId && present) {
      setLoadingCobrowse(true);
      sessionStart = Date.now();
      setShowInfo(true);
      setInfoMessage('Waiting for visitor');
      startSession = true;

      const visitorId = props.visitorId;
      const acceptTermsHandler = () => {
        if (startSession) {
          setShowError(false);
          setErrorMessage('');
          setAutoJoin(false);
        }
      };
      const declineTermsHandler = () => {
        if (startSession) {
          setShowInfo(false);
          setInfoMessage('');
          setShowError(true);
          setErrorMessage(ErrorUserMessages.PresenceDeclinedError);
          setLoadingCobrowse(false);
          startSession = false;
          setAutoJoin(false);
        }
      };
      await Agent.presenceStartSession(
        visitorId,
        acceptTermsHandler,
        declineTermsHandler
      );
    }

    // if (loadingCobrowse) {
    //   setShowInfo(true);
    //   setInfoMessage('Waiting for visitor');
    // }
  };
  
  const sendSMS = async () => {
    if (loadingMCS) {
      setLoadingMCS(false);
    } else if (!havePhoneNumber) {
      setHaveKey(false);
      setHavePhoneNumber(true);
    } else if (phoneNumber.length >= 9) {
      setLoadingMCS(true);
      startSession = true;
      sessionStart = Date.now();
      try {
        const invite = await sessionInvite(
          {
            baseUrl: baseurl[props.glanceBaseUrl],
            groupId: Agent.groupId,
            puid: Agent.puid,
            loginKey: Agent.loginKey,
          },
          {
            sessionType: 1,
            smsPhone: phoneNumber,
            agentName: Agent.puid,
            sessionParams: {
              camera: 'back',
              resolution: '352x288',
            },
          }
        );
        await startCobrowse(invite.sessionKey);
      } catch (error) {
        if (error?.name == 'ExternalServiceHandledError') {
          const userMessage =
            ErrorUserMessages.InviteSessionBusinessError.replace(
              '{0}',
              error.message
            );
          Agent.defaultErrorHandler(error, 'sendSMS', userMessage);
        } else {
          Agent.defaultErrorHandler(
            error,
            'sendSMS',
            ErrorUserMessages.InviteSessionError
          );
        }
      }
    }
  };

  const cancelSession = (cancelSessionReason: any) => {
    // TODO: potentially we need to support different reasons of cancel - like timeout, agent initiated etc
    if (startSession) {
      Agent.lookupServiceCancel();
      // NOTE: historical data - reporting flow, starts only with session end
      if (props.reportingCallback && Agent.sessionId) {
        const sessionEndTime = Date.now();
        setSessionEnd(sessionEndTime);
        const durationInSeconds = Math.floor(
          (sessionEndTime - sessionStart) / 1000
        );
        const sessionData: HistoricalReportingData = {
          sessionStart: new Date(sessionStart).toISOString(),
          sessionEnd: new Date(sessionEndTime).toISOString(),
          duration: durationInSeconds,
          sessionId: Agent.sessionId.toString(),
        };

        // TODO: implement session data from db
        // const sessionData = await getSessionData({
        //   baseUrl: `${props.glancebaseurl}`,
        //   groupId: props.groupid as string,
        //   apiKey: props.puid,
        //   visitorId: props.visitorid,
        //   start: sessionStart,
        //   end: sessionEnd,
        //   puid: props.puid,
        // });

        props.reportingCallback(sessionData);
      }

      inSession = false;
      setShowInfo(false);
      setCobrowseKey('');
      setPhoneNumber('');
      setAgentJoinUrl(null);
      setHaveKey(false);
      setHavePhoneNumber(false);
      setLoadingMCS(false);
      setLoadingCobrowse(false);
      Agent.resetSessionId();
      Agent.cancelEndSessionCheck();
      startSession = false;
    }
  };

  const endSession = async (endSessionReason: any) => {
    // TODO: potentially we need to support different reasons of cancel - like agent initiated, visitor initiated, error event etc
    if (startSession) {
      inSession = false;
      // NOTE: scenario when we hold the Agent window reference, we can post message or close the it
      if (Agent.hasAgentWindow()) {
        // NOTE: we're just closing window now if we've reference to it - but we can do a "right" postMessage(s) and keep window opened
        const windowClosed = Agent.closeAgentWindow();
      } else if (props.authMethod == AuthMethod.Loginkey) {
        // NOTE: the scenario when we don't have windows references and have server auth inplace
        if (props.visitorId && present) {
          await Agent.presenceEndSession(props.visitorId);
        } else if (!present && haveKey && cobrowseKey.length > 0) {
          await Agent.cobrowseEndSession(cobrowseKey);
        }
      }
      cancelSession(endSessionReason);
    }
  };

  const startCobrowse = async (key: string) => {
    if (startSession) {
      // TODO: Agent.js does not support tab (bug) so we cannot use joinSession yet
      // TODO: We also cannot use joinSession if customers require passing in an openWindow callback

      const agentJoinUrlRaw = await Agent.lookupService({
        cobrowseKey: key,
        debug: props.debugMode,
        sessionStateHandler: (isActiveSession) => {
          const reason = { reason: 'agent' };
          if (isActiveSession && !inSession) {
            inSession = true;
          } else if (!isActiveSession && (inSession || loadingCobrowse)) {
            cancelSession(reason);
          }
          if (!isActiveSession) {
            Agent.cancelEndSessionCheck();
          }
        },
      });

      // NOTE: we're intentionally double-checking here the startSession because session can be canceled already and we've not event-based here
      if (startSession) {
        if (!agentJoinUrlRaw) {
          setShowInfo(false);
          setShowError(true);
          setErrorMessage(ErrorUserMessages.LookupSessionNotFoundError);
          setLoadingCobrowse(false);
        } else {
          if (props.debugMode) {
            console.log(
              `GLANCE: opening ${props.openlocation} for session key: ${key}`
            );
          }
          const reportingOriginKey = 'origin';
          const reportingExternalIdKey = 'xid';
          const agentJoinUrlWithParams = new URL(agentJoinUrlRaw);
          agentJoinUrlWithParams.searchParams.set(reportingOriginKey, props.reportingOrigin);
          agentJoinUrlWithParams.searchParams.set(reportingExternalIdKey, props.reportingExternalId);
          const agentJoinUrl = agentJoinUrlWithParams.toString();
          switch (props.openlocation) {
            case 'iframe':
              setAgentJoinUrl(agentJoinUrl);
              break;
            case 'tab':
              if (openWindowCallback) openWindowCallback(agentJoinUrl);
              else Agent.openWindow(agentJoinUrl, '_blank', cancelSession);
              break;
            default:
              if (openWindowCallback) openWindowCallback(agentJoinUrl);
              else
                Agent.openWindow(
                  agentJoinUrl,
                  'width=800,height=800',
                  cancelSession
                );
          }

          setLoadingCobrowse(false);
          setShowInfo(false);
          setLoadingMCS(false);
          inSession = true;
        }
      }
    }
  };

  // NOTE: this cancelCobrowse here is handling "Cancel" and "Have Key" actions, needs to be refactored ... as well as join function and display attribute "dark magic" below
  const cancelCobrowse = () => {
    setHavePhoneNumber(false);
    setCobrowseKey('');
    setHaveKey(!haveKey);
    setLoadingCobrowse(false);
  };

  return (
    <AgentPluginLayout>
      {agentJoinUrl ? (
        <Cobrowse url={agentJoinUrl} />
      ) : (
        <>
          <Grid display={true}>
            <Grid display={true}>
              {props.gicon && <Logo display={props.gicon == undefined || props.gicon} /> }
              <Loader display={loadingCobrowse} />
              <CobrowseInput
                cobrowseKey={cobrowseKey}
                updateKey={(val) => setCobrowseKey(val)}
                display={cobrowseEnabled && (haveKeyDefault || haveKey) && !loadingCobrowse}
              />
              <CobrowseJoinButton
                inSession={inSession}
                present={presenceEnabled && present}
                haveKey={haveKeyDefault || haveKey}
                loadingCobrowse={loadingCobrowse}
                join={join}
                display={(haveKeyDefault || haveKey) || ((inSession || startSession) && loadingCobrowse) || (presenceEnabled && present)}
              />
              <CobrowseCancelButton
                haveKey={haveKeyDefault || haveKey}
                display={cobrowseEnabled && !haveKeyDefault && !inSession && !loadingCobrowse}
                cancel={cancelCobrowse}
              />
            </Grid>

            <Grid display={mobileCameraShare}>
              <Loader display={loadingMCS} />
              <MobileCameraShareInput
                phoneNumber={phoneNumber}
                updatePhoneNumber={(val) => setPhoneNumber(val)}
                display={havePhoneNumber}
              />
              <MobileCameraShareButton
                loadingMCS={loadingMCS}
                sendSMS={sendSMS}
              />
            </Grid>
          </Grid>
          <InfoMessage message={infoMessage} display={showInfo} />
          <ErrorMessage
            message={errorMessage}
            display={showError}
            dismiss={setShowError}
          />
        </>
      )}
    </AgentPluginLayout>
  );
};